import moment from 'moment';
import { TimeUtilities } from '../utilities/time-utilities';

export class DateTimeUtilities {
    /**
     * Sets the date time portion of a moment object to the date time given in a HTML5 formatted date time string
     *
     * @param target
     * @param dateTime
     */
    public static setDateTimeHtml5(target: moment.Moment, dateTime: string): moment.Moment {
        return DateTimeUtilities.setDateTimeMoment(target, moment(dateTime, moment.HTML5_FMT.DATETIME_LOCAL));
    }

    /**
     * Sets the date time portion of a moment object based on a source moment object
     *
     * @param target
     * @param dateTime
     */
    public static setDateTimeMoment(target: moment.Moment, dateTime: moment.Moment): moment.Moment {
        const dateTimeObject = dateTime.toObject();
        if (!target.isValid()) {
            target = moment();
        }

        target.set({
            years: dateTimeObject.years,
            months: dateTimeObject.months,
            date: dateTimeObject.date,
            hours: dateTimeObject.hours,
            minutes: dateTimeObject.minutes,
            seconds: dateTimeObject.seconds,
            milliseconds: dateTimeObject.milliseconds,
        });
        return target;
    }

    /**
     * Checks if the provided date is same as the next working day.
     *
     * @param currentDate current date
     * @param referenceDate the date that needs to be compared
     * @param isUTC
     * @returns boolean
     */
    public static isDateNextWorkingDay(
        currentDate: moment.Moment,
        referenceDate: moment.Moment,
        isUTC = false,
    ): boolean {
        const nextWorkingDay = TimeUtilities.getNextWorkingDayDateTime(currentDate, 1, isUTC);
        return nextWorkingDay.isSame(referenceDate, 'day');
    }

    /**
     * Checks if the current date and time is within the date-time range provided.
     *
     * @param currentDateTime current date and time
     * @param startDatetime start date and time of the range
     * @param endDateTime end date and time of the range
     * @returns boolean
     */
    public static isDateAndTimeInRange(
        currentDateTime: moment.Moment,
        startDatetime: moment.Moment,
        endDateTime: moment.Moment,
    ): boolean {
        return (
            currentDateTime.isSameOrAfter(startDatetime, 'minute') &&
            currentDateTime.isSameOrBefore(endDateTime, 'minute')
        );
    }

    /**
     * Checks if the provided date is in the future, i.e. after the next working day.
     *
     * @param currentDate current date and time
     * @param referenceDate the date that needs to be compared
     * @param isUTC
     * @returns boolean
     */
    public static isDateAfterNextWorkingDay(
        currentDate: moment.Moment,
        referenceDate: moment.Moment,
        isUTC = false,
    ): boolean {
        const nextWorkingDay = TimeUtilities.getNextWorkingDayDateTime(currentDate, 1, isUTC);
        return referenceDate.isSameOrAfter(nextWorkingDay.add(1, 'day'), 'day');
    }
}
